import "../reports.css";
import { ReactNode, useEffect, useState } from "react";
import { useApiFetch, useDimensions, useLanguage } from "../../../hooks";
import {
  ReportStatusCardDataProps,
  ReportStatusCardsProps,
  ReportStatusType,
  sortReportRows,
} from "../reports.props";
import {
  BoardBody,
  BoardDotStatus,
  BoardHead,
  BoardTd,
  BoardTdMobileItem,
  BoardTh,
  BoardTr,
} from "../../../components";
import { API_URL } from "../../../services";
import { vars } from "../../../assets";
import {
  filterData,
  formatAmount,
  formatDateTimeToLocal,
  formatDateToLocal,
  toISODateFromDMY,
} from "../../../utils";

interface CardColumn {
  key: keyof ReportStatusCardDataProps;
  header: string;
  render: (card: ReportStatusCardDataProps) => ReactNode;
  alignRight?: boolean; // columnas numéricas
}

type SortState = {
  key: keyof ReportStatusCardDataProps;
  direction: "asc" | "desc";
};

const STATUS_COLORS: Record<
  ReportStatusType,
  { primary: string; secondary: string }
> = {
  active: { primary: "#1FC16B", secondary: "#1FC16B1A" },
  completed: { primary: "#FFFFFF", secondary: "#1FC16B" },
  cancelled: { primary: "#D00416", secondary: "#FB37481A" },
  refunded: { primary: "#D00416", secondary: "#FB37481A" },
  expired: { primary: "#4A4A4A", secondary: "#DFB40033" },
};

const text = (value: unknown): ReactNode => <p>{String(value ?? "")}</p>;

// "Estado tarjetas": columnas y orden de docs/Estado Tarjetas.xlsx salvo KBInfo,
// que ya no se usa, más SALDO INICIAL (importe de la activación). Solo tarjetas vendidas.
const ReportStatusCards: React.FC<ReportStatusCardsProps> = ({
  search = "",
  filters,
  setExportDisabled,
  setErrorFetchData,
  onRowsChange,
}) => {
  const { t } = useLanguage();
  const { width } = useDimensions();
  const thead = t.reportsScreen.table.thead;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [fetchData, , loading, error] = useApiFetch<
    ReportStatusCardDataProps[]
  >([]);
  const [cards, setCards] = useState<ReportStatusCardDataProps[]>([]);
  const [sort, setSort] = useState<SortState | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const renderStatus = (card: ReportStatusCardDataProps) => {
    const colors = STATUS_COLORS[card.status] ?? {
      primary: vars.colorBlack,
      secondary: vars.colorWhiteMedium,
    };
    return (
      <BoardDotStatus
        pColor={colors.primary}
        sColor={colors.secondary}
        text={t.reportsScreen.table.tbody[card.status]}
      />
    );
  };

  const columns: CardColumn[] = [
    { key: "status", header: thead.status, render: renderStatus },
    {
      key: "brandName",
      header: thead.brand,
      render: (c) => <p className="board-bold">{c.brandName}</p>,
    },
    {
      key: "expirationDate",
      header: thead.expiration,
      render: (c) =>
        text(c.expirationDate ? formatDateToLocal(c.expirationDate) : ""),
    },
    {
      key: "activationCode",
      header: thead.activationCode,
      render: (c) => text(c.activationCode),
    },
    {
      key: "lastActivation",
      header: thead.lastActivation,
      render: (c) => text(formatDateTimeToLocal(c.lastActivation)),
    },
    {
      key: "lastRedemption",
      header: thead.lastRedemption,
      render: (c) => text(formatDateTimeToLocal(c.lastRedemption)),
    },
    {
      key: "amount",
      header: thead.initialBalance,
      render: (c) => text(formatAmount(c.amount)),
      alignRight: true,
    },
    {
      key: "availableBalance",
      header: thead.availableBalance,
      render: (c) => text(formatAmount(c.availableBalance)),
      alignRight: true,
    },
  ];

  const matchesSearch = (card: ReportStatusCardDataProps): boolean => {
    const needle = search.toLowerCase();
    return [
      card.brandName,
      card.activationCode,
      card.retailer,
      card.ean,
      t.reportsScreen.table.tbody[card.status],
      card.expirationDate ? formatDateToLocal(card.expirationDate) : "",
      formatDateTimeToLocal(card.lastActivation),
      formatDateTimeToLocal(card.lastRedemption),
      card.availableBalance,
    ].some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(needle)
    );
  };

  // Ciclo de orden por columna: ascendente -> descendente -> sin orden.
  const toggleSort = (key: keyof ReportStatusCardDataProps) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, direction: "asc" };
      return prev.direction === "asc" ? { key, direction: "desc" } : null;
    });
  };

  // "Caduca antes de": solo tarjetas con caducidad conocida hasta ese día incluido
  // (las caducadas y las próximas a caducar).
  const expiresBefore = toISODateFromDMY(filters.expiresBefore ?? "");
  const filtered = filterData(cards, filters).filter(
    (card) =>
      (search.length === 0 || matchesSearch(card)) &&
      (!expiresBefore ||
        (!!card.expirationDate &&
          card.expirationDate.slice(0, 10) <= expiresBefore))
  );
  const rows = sort
    ? sortReportRows(filtered, sort.key, sort.direction)
    : filtered;

  useEffect(() => setIsMobile(width <= 650), [width]);
  useEffect(() => setExportDisabled(isMobile), [isMobile]);

  useEffect(() => {
    // CARGAR DATOS: todos los filtros de esta pestaña (retailer, estado,
    // caducidad, búsqueda...) se aplican en el cliente.
    // El flag evita que una respuesta obsoleta (p.ej. la del doble-render de
    // StrictMode) sobrescriba el estado con datos de una petición más reciente.
    let cancelled = false;
    fetch(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, []);

  const fetch = async (isCancelled: () => boolean) => {
    const d = await fetchData(API_URL.CARDS(), { method: "GET" });
    if (isCancelled()) return;
    setCards(d?.data ?? []);
    onRowsChange?.(d?.data ?? []);
  };

  useEffect(() => {
    error?.length && setErrorFetchData(true);
  }, [error]);

  return (
    <>
      <BoardHead>
        <BoardTr>
          {isMobile ? (
            <BoardTh
              onClick={() => toggleSort("activationCode")}
              text={thead.activationCode}
            />
          ) : (
            columns.map((column) => (
              <BoardTh
                key={column.key}
                className={column.alignRight ? "board-align-right" : undefined}
                onClick={() => toggleSort(column.key)}
                text={column.header}
              />
            ))
          )}
        </BoardTr>
      </BoardHead>

      <BoardBody isMobile={isMobile} isLoading={loading}>
        {rows.map((card, index) => {
          const id: string = `${card.id}-${index}`;
          return (
            <BoardTr key={`reports-card-${id}`}>
              {isMobile ? (
                <BoardTd
                  hasClassNameW100
                  hasClassNameDirection={false}
                  mobile={{
                    isMobile: true,
                    isActive: activeId === id,
                    onClick: () =>
                      setActiveId((prev) => (prev === id ? null : id)),
                    text: card.activationCode ?? "",
                  }}
                >
                  {columns.map((column) => (
                    <BoardTdMobileItem key={column.key} title={column.header}>
                      {column.render(card)}
                    </BoardTdMobileItem>
                  ))}
                </BoardTd>
              ) : (
                columns.map((column) => (
                  <BoardTd
                    key={column.key}
                    className={column.alignRight ? "board-align-right" : ""}
                    hasClassNameDirection={column.key !== "status"}
                  >
                    {column.render(card)}
                  </BoardTd>
                ))
              )}
            </BoardTr>
          );
        })}
      </BoardBody>
    </>
  );
};

export default ReportStatusCards;
